/**
 * armory.ts — the equipment panel, opened with I.
 *
 * Laid out as an equipment DOLL, the way an RPG inventory has looked since
 * RuneScape: a live 3D figure in the middle with a square per slot arranged
 * around it, each showing what is in that slot. Click a square and the options
 * for that slot open next to it; the figure never goes away, so you are always
 * choosing against the thing you are dressing rather than against a list.
 *
 * That is the whole reason for the layout. Sixteen pieces described in text is
 * a spreadsheet, and the only way to choose between a hauberk and a cuirass is
 * to see them on the man who has to fight in them. The doll builds a real
 * `Figure` and poses it with the real poser, so what you are looking at is what
 * will walk out into the hall — not a second, approximate model that can drift.
 *
 * The client never dresses itself. A pick goes up as a message and comes back
 * down in the snapshot, so what you see on your own duellist is what your
 * opponent sees on them, and the stats shown here are the ones the server
 * applies. The panel holds the SELECTION locally so it stays responsive, and
 * `setEquipped` reconciles it against what the server actually granted.
 *
 * Opening releases the pointer lock — you cannot click a square while the mouse
 * is driving the camera — and the game re-locks on the next click in the world.
 */

import * as THREE from 'three';
// The same faces the front door uses. Imported HERE rather than leaned on via
// `landing.ts`: the armoury opens mid-game, long after the landing is gone, and
// depending on another module's side effect for your typeface is a font that
// vanishes the day someone reorders an import. Vite dedupes it.
import './vendor/fonts.css';
import {
  EQUIP_SLOTS,
  FEEL,
  PIECES,
  WEIGHT_CLASSES,
  armorAt,
  defaultLoadout,
  equipped,
  loadoutMoveMult,
  loadoutWeight,
  piecesForSlot,
  suitOf,
  type EquipSlot,
  type HitZone,
  type Loadout,
  type Piece,
  type PieceId,
} from '@dueler/shared';
import { createEnvironment } from '../env.js';
import { createFigure, disposeFigure, poseFigure, wearKit, type Figure } from '../figure.js';

/** What each slot is called on its square. */
const SLOT_NAMES: Record<EquipSlot, string> = {
  helm: 'Head',
  shoulders: 'Shoulders',
  chest: 'Body',
  gauntlets: 'Hands',
  legs: 'Legs',
  boots: 'Feet',
  weapon: 'Weapon',
};

/**
 * Where each slot's square sits in the equipment tree.
 *
 * The shape every RPG has used since RuneScape: a column down the body with
 * the things that hang off it either side, connected by struts. Read top to
 * bottom it is a person — helm, shoulders, chest, legs, boots — with the
 * weapon and the gauntlets branching off the chest where the arms are.
 *
 * The point of the shape is that you never read a label to find a slot.
 */
const SLOT_CELL: Record<EquipSlot, string> = {
  helm: 'helm',
  shoulders: 'shoulders',
  weapon: 'weapon',
  chest: 'chest',
  gauntlets: 'gauntlets',
  legs: 'legs',
  boots: 'boots',
};

/**
 * Which struts to draw from each square back towards the spine of the tree.
 *
 * Drawn as pseudo-elements reaching into the grid gap rather than as a
 * background image, so they stay put at any panel width.
 */
const SLOT_LINK: Record<EquipSlot, string> = {
  helm: '',
  shoulders: 'link-up',
  chest: 'link-up',
  weapon: 'link-right',
  gauntlets: 'link-left',
  legs: 'link-up',
  boots: 'link-up',
};

/** Which zone's damage a slot's armour applies to, for the readout. */
const SLOT_ZONE: Partial<Record<EquipSlot, HitZone>> = {
  helm: 'head',
  chest: 'torso',
  legs: 'legs',
  boots: 'feet',
  gauntlets: 'arm',
  shoulders: 'arm',
};

/**
 * A glyph per slot, drawn as inline SVG.
 *
 * Silhouettes rather than words: a square with "Body" written on it is a form
 * field, and a square with a breastplate on it is a piece of equipment. They
 * are tinted by whatever is equipped, so a row of squares reads as a harness at
 * a glance and an empty slot reads as empty.
 */
const SLOT_GLYPH: Record<EquipSlot, string> = {
  helm: '<path d="M6 13a10 10 0 0 1 20 0v6a4 4 0 0 1-4 4h-1v-4H11v4h-1a4 4 0 0 1-4-4z"/><rect x="11" y="14" width="10" height="3" rx="1"/>',
  shoulders:
    '<path d="M4 20a7 7 0 0 1 24 0v3a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z"/><path d="M4 21h24" stroke-width="1.5" fill="none"/>',
  chest:
    '<path d="M8 6h16l2 7-3 14a2 2 0 0 1-2 2H11a2 2 0 0 1-2-2L6 13z"/><path d="M16 8v18" stroke-width="1.5" fill="none"/>',
  gauntlets:
    '<path d="M11 5h10v9l3 3v8a3 3 0 0 1-3 3h-10a3 3 0 0 1-3-3v-8l3-3z"/><path d="M8 20h16" stroke-width="1.5" fill="none"/>',
  legs: '<path d="M9 4h14l-1 11-2 14h-4l-1-12-1 12h-4L9 15z"/>',
  boots:
    '<path d="M10 4h7v14l8 5v6H10z"/><path d="M10 23h15" stroke-width="1.5" fill="none"/>',
  weapon:
    '<path d="M15 2h2l1 17h-4z"/><rect x="8" y="19" width="16" height="3" rx="1"/><rect x="14" y="22" width="4" height="7" rx="1"/><circle cx="16" cy="30" r="2"/>',
};

/** Hex for a colour number, for inline styles. */
const hex = (colour: number): string => `#${colour.toString(16).padStart(6, '0')}`;

export interface Armory {
  toggle(): void;
  close(): void;
  /** Reflects what the server says is being worn, which is the only truth. */
  setEquipped(kit: Loadout): void;
  /** Advances the doll. Cheap and a no-op while closed. */
  update(now: number): void;
  readonly open: boolean;
  dispose(): void;
}

export function createArmory(onEquip: (kit: Loadout) => void): Armory {
  /** What the panel is showing. Optimistic; `setEquipped` corrects it. */
  let selection: Loadout = defaultLoadout();
  /**
   * The last loadout actually rendered, as a comparable key.
   *
   * `setEquipped` is called from every snapshot — sixty times a second — and
   * without this it rebuilt the whole panel each time. That was not merely
   * wasteful: it made the chooser IMPOSSIBLE TO CLICK. A `click` only fires
   * when mousedown and mouseup land on the same element, and the option button
   * under the cursor was being destroyed and recreated between the two.
   *
   * It also rebuilt every piece of kit geometry on the doll at 60Hz.
   */
  let rendered = '';
  let open = false;
  /** Which slot's chooser is open, if any. */
  let openSlot: EquipSlot | undefined;

  const root = document.createElement('div');
  root.className = 'armory';
  root.hidden = true;

  const frame = document.createElement('div');
  frame.className = 'armory-frame';
  root.appendChild(frame);

  const header = document.createElement('div');
  header.className = 'armory-header';
  header.innerHTML = '<span class="armory-title">ARMOURY</span>';
  const hint = document.createElement('span');
  hint.className = 'armory-hint';
  hint.textContent = 'I to close';
  header.appendChild(hint);
  frame.appendChild(header);

  // Body on the left, equipment tree on the right, numbers underneath.
  const columns = document.createElement('div');
  columns.className = 'armory-columns';
  frame.appendChild(columns);

  const stage = document.createElement('div');
  stage.className = 'armory-stage';
  columns.appendChild(stage);

  const doll = document.createElement('div');
  doll.className = 'armory-doll';
  columns.appendChild(doll);

  interface SlotUi {
    button: HTMLButtonElement;
    icon: SVGSVGElement;
    label: HTMLSpanElement;
  }
  const slotUi = new Map<EquipSlot, SlotUi>();

  for (const slot of EQUIP_SLOTS) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `armory-slot ${SLOT_LINK[slot]}`.trim();
    button.style.gridArea = SLOT_CELL[slot];
    button.title = SLOT_NAMES[slot];

    const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    icon.setAttribute('viewBox', '0 0 32 32');
    icon.classList.add('armory-glyph');
    icon.innerHTML = SLOT_GLYPH[slot];
    button.appendChild(icon);

    const label = document.createElement('span');
    label.className = 'armory-slot-label';
    label.textContent = SLOT_NAMES[slot];
    button.appendChild(label);

    button.addEventListener('click', () => {
      showChooser(openSlot === slot ? undefined : slot);
    });
    doll.appendChild(button);
    slotUi.set(slot, { button, icon, label });
  }

  // ---- the chooser, opened by a slot ----
  const chooser = document.createElement('div');
  chooser.className = 'armory-chooser';
  chooser.hidden = true;
  frame.appendChild(chooser);

  // Anywhere else dismisses the chooser, which is what makes it feel like a
  // menu rather than a mode you have to leave deliberately.
  //
  // Decided by CONTAINMENT rather than by stopping propagation on everything
  // that should not close it. The stopPropagation version needs every new
  // interactive element to remember to opt out, and the one that forgets is a
  // menu that shuts as you use it.
  root.addEventListener('click', (event) => {
    const target = event.target as Node | null;
    if (target !== null && (chooser.contains(target) || insideSlot(target))) return;
    showChooser(undefined);
  });

  const insideSlot = (node: Node): boolean => {
    for (const { button } of slotUi.values()) if (button.contains(node)) return true;
    return false;
  };

  function showChooser(slot: EquipSlot | undefined): void {
    openSlot = slot;
    chooser.hidden = slot === undefined;
    for (const [id, ui] of slotUi) ui.button.classList.toggle('active', id === slot);
    if (slot === undefined) return;

    chooser.textContent = '';
    const title = document.createElement('div');
    title.className = 'armory-chooser-title';
    title.textContent = SLOT_NAMES[slot];
    chooser.appendChild(title);

    // Bare first, then light → heavy. The weapon slot has no bare option: a
    // duellist with no blade is not a state the game has anything to say about.
    const entries: (Piece | undefined)[] = slot === 'weapon' ? [] : [undefined];
    for (const piece of piecesForSlot(slot)) entries.push(piece);

    for (const piece of entries) {
      const id = piece?.id ?? null;
      const option = document.createElement('button');
      option.type = 'button';
      option.className = `armory-option${piece ? ` armory-${piece.weightClass}` : ' armory-bare'}`;
      option.classList.toggle('equipped', selection[slot] === id);

      const swatch = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      swatch.setAttribute('viewBox', '0 0 32 32');
      swatch.classList.add('armory-glyph', 'armory-option-glyph');
      swatch.innerHTML = SLOT_GLYPH[slot];
      if (piece !== undefined) swatch.style.color = hex(piece.palette.steel);
      option.appendChild(swatch);

      const text = document.createElement('span');
      text.className = 'armory-option-text';
      const name = document.createElement('span');
      name.className = 'armory-option-name';
      name.textContent = piece?.name ?? 'Nothing';
      const blurb = document.createElement('span');
      blurb.className = 'armory-option-blurb';
      blurb.textContent = piece?.blurb ?? 'Bare. Quick, and you will feel it.';
      text.append(name, blurb);
      option.appendChild(text);

      if (piece !== undefined) {
        const numbers = document.createElement('span');
        numbers.className = 'armory-option-stats';
        // The two numbers that decide the pick, on the option itself, so
        // comparing three of them is one glance rather than three hovers.
        numbers.innerHTML =
          `<b>${Math.round(piece.stats.armor * 100)}%</b><i>armour</i>` +
          `<b>${piece.stats.weight}</b><i>kg</i>`;
        option.appendChild(numbers);
      }

      option.addEventListener('click', () => {
        choose({ ...selection, [slot]: id });
        showChooser(slot);
      });
      chooser.appendChild(option);
    }
  }

  // ---- full sets and stats ----
  const footer = document.createElement('div');
  footer.className = 'armory-footer';
  frame.appendChild(footer);

  const suits = document.createElement('div');
  suits.className = 'armory-suits';
  // Strip first, then light → heavy: it reads as a scale that way, nothing then
  // progressively more of it. Strip leaves the sword.
  const suitButton = (label: string, kit: () => Loadout, extra: string): void => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `armory-suit ${extra}`;
    button.textContent = label;
    button.addEventListener('click', () => {
      showChooser(undefined);
      choose(kit());
    });
    suits.appendChild(button);
  };
  suitButton('Strip', defaultLoadout, 'armory-strip');
  for (const weightClass of WEIGHT_CLASSES) {
    suitButton(weightClass, () => suitOf(weightClass), `armory-${weightClass}`);
  }
  const stats = document.createElement('dl');
  stats.className = 'armory-stats';
  footer.append(stats, suits);

  document.body.appendChild(root);

  const viewer = createViewer(stage);

  /** Applies a pick: shows it immediately, and asks the server for it. */
  function choose(kit: Loadout): void {
    selection = kit;
    redraw();
    onEquip(kit);
  }

  function redraw(): void {
    rendered = JSON.stringify(selection);
    for (const [slot, ui] of slotUi) {
      const piece = equipped(selection, slot);
      ui.button.classList.toggle('filled', piece !== undefined);
      ui.icon.style.color = piece === undefined ? '' : hex(piece.palette.steel);
      ui.label.textContent = piece?.name ?? SLOT_NAMES[slot];
      ui.button.title = piece === undefined ? `${SLOT_NAMES[slot]}: empty` : piece.blurb;
    }
    viewer?.wear(selection);
    drawStats();
  }

  function drawStats(): void {
    stats.textContent = '';
    const rows: [string, string][] = [
      ['Weight', `${loadoutWeight(selection).toFixed(1)} kg`],
      // Shown as what it COSTS rather than what is left: "−8%" is the decision
      // being made, and "92%" is arithmetic you have to do first.
      ['Footwork', formatDelta(loadoutMoveMult(selection) - 1)],
    ];
    for (const zone of ['head', 'torso', 'legs', 'feet', 'arm'] as const) {
      // The number that matters is what a hit actually costs you there, so the
      // zone multiplier is folded in rather than left for the player to apply.
      const taken = zoneMult(zone) * (1 - armorAt(selection, zone));
      rows.push([ZONE_LABEL[zone], `${Math.round(taken * 100)}%`]);
    }
    for (const [key, value] of rows) {
      const dt = document.createElement('dt');
      dt.textContent = key;
      const dd = document.createElement('dd');
      dd.textContent = value;
      stats.append(dt, dd);
    }
  }

  const setOpen = (next: boolean): void => {
    open = next;
    root.hidden = !next;
    if (!next) showChooser(undefined);
    if (next && document.pointerLockElement !== null) document.exitPointerLock();
    if (next) viewer?.resize();
  };

  redraw();

  return {
    toggle: () => setOpen(!open),
    close: () => setOpen(false),
    setEquipped(kit) {
      // Snapshots arrive constantly and almost never change what is worn; only
      // touch the DOM when the answer is actually different. See `rendered`.
      const key = JSON.stringify(kit);
      if (key === rendered) return;
      selection = { ...kit };
      redraw();
      if (openSlot !== undefined) showChooser(openSlot);
    },
    update(now) {
      if (!open) return;
      viewer?.update(now);
    },
    get open() {
      return open;
    },
    dispose() {
      viewer?.dispose();
      root.remove();
    },
  };
}

const ZONE_LABEL: Record<HitZone, string> = {
  head: 'Head hits',
  torso: 'Body hits',
  legs: 'Leg hits',
  feet: 'Foot hits',
  arm: 'Arm hits',
};

/**
 * A percentage with an explicit sign.
 *
 * Plain ASCII `-`, and `0%` rather than a dash at zero. The typographic minus
 * (U+2212) and the em dash are outside the bold subset of the inlined font, so
 * they would silently fall back to a system face and render at a different
 * weight beside the digits they belong to.
 */
function formatDelta(fraction: number): string {
  const percent = Math.round(fraction * 100);
  if (percent === 0) return '0%';
  return `${percent > 0 ? '+' : '-'}${Math.abs(percent)}%`;
}

/** Mirrors `zoneMultiplier` for the readout. */
function zoneMult(zone: HitZone): number {
  switch (zone) {
    case 'head':
      return FEEL.HEAD_MULT;
    case 'legs':
      return FEEL.LEG_MULT;
    case 'feet':
      return FEEL.FOOT_MULT;
    case 'arm':
      return FEEL.ARM_MULT;
    case 'torso':
      return 1;
  }
}

// ---------------------------------------------------------------------------
// the doll
// ---------------------------------------------------------------------------

interface Viewer {
  wear(kit: Loadout): void;
  update(now: number): void;
  resize(): void;
  dispose(): void;
}

/**
 * A rotating, draggable figure in a small canvas.
 *
 * Returns undefined where WebGL is unavailable — a headless test, or a machine
 * whose context creation fails — and every caller null-checks, so the panel
 * still works as a list of slots. Losing the picture must not lose the armoury.
 */
function createViewer(parent: HTMLElement): Viewer | undefined {
  let renderer: THREE.WebGLRenderer;
  try {
    renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
  } catch {
    return undefined;
  }

  const canvas = renderer.domElement;
  canvas.className = 'armory-canvas';
  parent.appendChild(canvas);

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(32, 1, 0.1, 40);

  // Lit like a shop, not like the dungeon: a key from the front-left so plate
  // catches, a cool rim behind to lift the silhouette off the panel, and enough
  // fill that a black gambeson is still a shape.
  const key = new THREE.DirectionalLight(0xfff0d8, 1.9);
  key.position.set(-2.2, 3.2, 3.4);
  const rim = new THREE.DirectionalLight(0x86a8d8, 1.15);
  rim.position.set(2.6, 2.2, -3);
  scene.add(key, rim, new THREE.HemisphereLight(0x9fb2cc, 0x2a2620, 1.0));

  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 0.98;
  // The doll needs the same environment the hall has, or plate that reads as
  // steel in the fight reads as plastic in the panel you choose it from.
  scene.environment = createEnvironment(renderer) ?? null;

  // `true`: this is your own duellist, so it wears your colours.
  const figure: Figure = createFigure(true);
  // Lifted so the SOLES sit on y=0. A figure is built about its capsule centre,
  // which put its feet half a body height below the origin — frame it as though
  // the feet were at zero and the legs run off the bottom of the canvas.
  figure.root.position.y = FEEL.BODY_HEIGHT / 2;
  scene.add(figure.root);

  // Standing still, guard down: the pose you spend most of a duel in, and the
  // one that shows the most of what is worn.
  // Standing perfectly still: stride zero, so the legs are planted and every
  // piece of leg armour is shown where it sits at rest.
  const REST = {
    anim: { id: 'idle' as const, t: 0, angle: 0 },
    blocking: false,
    pitch: 0,
    gaitPhase: 0,
    gaitStride: 0,
  };

  // Turntable, plus drag to take it over. The idle spin is what tells you the
  // thing is three-dimensional at all; the drag is for looking at the back.
  let spin = 0.5;
  let dragging = false;
  let dragFrom = 0;
  let dragSpin = 0;
  let lastFrame: number | undefined;

  canvas.addEventListener('pointerdown', (event) => {
    event.stopPropagation();
    dragging = true;
    dragFrom = event.clientX;
    dragSpin = spin;
    canvas.setPointerCapture(event.pointerId);
  });
  canvas.addEventListener('pointermove', (event) => {
    if (!dragging) return;
    spin = dragSpin + (event.clientX - dragFrom) * 0.012;
  });
  const endDrag = (): void => {
    dragging = false;
  };
  canvas.addEventListener('pointerup', endDrag);
  canvas.addEventListener('pointercancel', endDrag);

  /** Body heights to fit in frame. Just over 1, so there is air top and bottom. */
  const FRAMING = 1.16;

  function resize(): void {
    const width = Math.max(1, parent.clientWidth);
    const height = Math.max(1, parent.clientHeight || Math.round(width * 1.5));
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(width, height, false);
    camera.aspect = width / height;

    // The distance is SOLVED rather than picked, so the whole figure is in
    // frame at any panel size — head to sabatons, which matters here and never
    // does in the game view, because boots are a slot you have to be able to
    // see. A hand-tuned distance is a crop waiting for someone to resize.
    const wanted = FEEL.BODY_HEIGHT * FRAMING;
    const vertical = 2 * Math.tan((camera.fov / 2) * THREE.MathUtils.DEG2RAD);
    // If the canvas is ever wider than it is tall, width becomes the binding
    // constraint and the fit has to be taken on that axis instead.
    const distance = wanted / Math.min(vertical, vertical * camera.aspect);

    const eye = FEEL.BODY_HEIGHT * 0.5;
    camera.position.set(0, eye, distance);
    camera.lookAt(0, eye, 0);
    camera.updateProjectionMatrix();
  }
  resize();

  return {
    wear(kit) {
      wearKit(figure, kit, true);
      poseFigure(figure, REST);
    },
    update(now) {
      const dt = lastFrame === undefined ? 0 : Math.min(0.05, (now - lastFrame) / 1000);
      lastFrame = now;
      if (!dragging) spin += dt * 0.45;
      figure.root.rotation.y = spin;
      // Re-posed every frame rather than once on a change: it costs almost
      // nothing and means the doll can never show a stale pose from before the
      // last swap.
      poseFigure(figure, REST);
      renderer.render(scene, camera);
    },
    resize,
    dispose() {
      disposeFigure(figure);
      renderer.dispose();
      canvas.remove();
    },
  };
}
